/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { PlatformType } from "../models/http/calculator/computing-platform.model";
import { ChipOption } from "../models/ui/chip-option.model";
import { Color } from "../models/ui/colors.model";
import { DashboardTabConfig } from "../models/ui/dashboard-tab.model";
import { Role } from "../models/ui/role.model";
import { SelectOption } from "../models/ui/select-option.model";
import { SidebarItem } from "../models/ui/sidebar-item.model";
import { GRANTS } from "./grants.constants";

export const PATHS = {
	DASHBOARD: "dashboard",
	IMPACT: "impact",
	SOFTWARE_ASSETS: "software-assets",
	ENVIRONMENTAL_IMPACT: "environmental-impact",
	COMPONENT_EMISSIONS: "component-emissions",
	APPLICATIONS: "applications",
	LOCATIONS: "locations",
	SOFTWARE_CARBON_INTENSITY: "software-carbon-intensity",
	ENERGY_CONSUMPTION: "energy-consumption",
	DATA_CENTRE: "data-centre",
	CLOUD: "cloud",
	HARDWARE_ASSETS: "hardware-assets",
	HARDWARE_ASSETS_REALTIME: "hardware-assets-realtime",
	NETWORK: "network",
	AI: "ai",
	TOOLBOX: "toolbox",
	STRATEGIC_APPLICATIONS: "strategic-applications",
	WHAT_IF: "toolbox/what-if",
	WHAT_IF_CONFIGURATION: "toolbox/what-if/configuration",
	CALCULATOR: "toolbox/calculator",
	ANOMALIES_DETECTION: "toolbox/anomalies-detection",
	ANOMALIES_DETECTION_DETAILS: "toolbox/anomalies-detection/details",
	REGIONAL_EMISSIONS: "regional-emissions",
	USERS: "users",
	// ALERTS: "alerts",
	PRIVACY_STATEMENT: "privacy-statement",
	TERMS_OF_USE: "terms-of-use"
};

export const COLORS: Record<Color, string> = {
	PATTERN: "rgba(7, 15, 38, 0.8)",
	SMART_NAVY: "#070f26",
	FUTURE_BLUE: "#0072bc",
	WHITE: "#ffffff",
	TEXT_GRAY: "#2e404d",
	FUTURE_BLUE_50: "#19a3fc",
	FUTURE_BLUE_150: "#005b96",
	TURQUOISE: "#00dfed",
	GREEN: "#00cb5d",
	BLACK: "#000000",
	YELLOW: "#ffc400",
	ORANGE: "#ff7a00",
	SMART_NAVY_100: "#192035",
	SMART_NAVY_50: "#31384c",
	GREY_100: "#949494",
	GREY_50: "#e8e8e8",
	TURQUOISE_100: "#009aa4",
	TURQUOISE_50: "#7bf7ff",
	GREEN_150: "#068941",
	GREEN_50: "#38f990",
	YELLOW_100: "#ffdb66",
	ORANGE_150: "#b22000",
	YELLOW_50: "#ffedb2",
	ORANGE_50: "#e42600"
};

export const DASHBOARD_TABS_MAP: Record<string, number> = {
	impact: 0,
	"software-assets": 1,
	"strategic-applications": 1,
	cloud: 2,
	"hardware-assets": 3,
	"hardware-assets-realtime": 3,
	"data-centre": 4,
	ai: 5
};

export const COMMON_TABS: DashboardTabConfig[] = [
	{ label: "impact", pages: [{ pageName: "impact", route: PATHS.IMPACT }] },
	{
		label: "applications",
		pages: [
			{ pageName: "swabl", route: PATHS.STRATEGIC_APPLICATIONS },
			{ pageName: "swart", route: PATHS.SOFTWARE_ASSETS }
		]
	},
	{ label: "cloud", pages: [{ pageName: "cloud", route: PATHS.CLOUD }] },
	{
		label: "hardware assets",
		pages: [
			{ pageName: "hwabl", route: PATHS.HARDWARE_ASSETS },
			{ pageName: "hwart", route: PATHS.HARDWARE_ASSETS_REALTIME }
		]
	},
	{
		label: "data centers",
		pages: [{ pageName: "dc", route: PATHS.DATA_CENTRE }],
		requiresDcEnabled: true
	},
	{ label: "ai", pages: [{ pageName: "ai", route: PATHS.AI }] }
];

export const SIDEBAR_ITEMS: SidebarItem[] = [
	{
		label: "DASHBOARD",
		icon: "icon-dashboard",
		routerLink: `/${PATHS.DASHBOARD}`,
		isActive: false
	},
	{
		label: "TOOLBOX",
		icon: "icon-tool-kit",
		routerLink: `/${PATHS.TOOLBOX}`,
		isActive: false,
		grant: GRANTS.toolbox.tab
	},
	{
		label: "REGIONAL EMISSIONS",
		icon: "icon-map",
		routerLink: `/${PATHS.REGIONAL_EMISSIONS}`,
		isActive: false,
		grant: GRANTS.regionalEmissions.tab
	},
	{
		label: "USERS",
		icon: "icon-population",
		routerLink: `/${PATHS.USERS}`,
		isActive: false,
		roles: [Role.ADMIN]
	}
	// {
	// 	label: "ALERTS",
	// 	icon: "icon-info",
	// 	routerLink: `/${PATHS.ALERTS}`,
	// 	isActive: false
	// }
];

export const REGIONS: SelectOption[] = [
	{ label: "Associated Electric Coop Inc", value: "AECI" },
	{ label: "Alberta Electric System Operator", value: "AESO" },
	{ label: "Austria", value: "AT" },
	{ label: "Avista Corp", value: "AVA" },
	{ label: "Arizona Public Service Co", value: "AZPS" },
	{ label: "Balancing Authority of Northern California", value: "BANC" },
	{ label: "BC Hydro", value: "BCHYDRO" },
	{ label: "Belgium", value: "BE" },
	{ label: "Bonneville Power Administration", value: "BPA" },
	{ label: "California Independent System Operator", value: "CAISO" },
	{ label: "PUD No 1 of Chelan County", value: "CHPD" },
	{ label: "Duke Energy Progress East", value: "CPLE" },
	{ label: "Duke Energy Progress West", value: "CPLW" },
	{ label: "Czechia", value: "CZ" },
	{ label: "Germany", value: "DE" },
	{ label: "Denmark", value: "DK" },
	{ label: "PUD No 1 of Douglas County", value: "DOPD" },
	{ label: "Duke Energy Carolinas", value: "DUK" },
	{ label: "Estonia", value: "EE" },
	{ label: "Greece", value: "EL" },
	{ label: "El Paso Electric", value: "ELE" },
	{ label: "ERCOT ISO", value: "ERCOT" },
	{ label: "Spain", value: "ES" },
	{ label: "Finland", value: "FI" },
	{ label: "Florida Municipal Power Pool", value: "FMPP" },
	{ label: "Duke Energy Florida", value: "FPC" },
	{ label: "Florida Power & Light", value: "FPL" },
	{ label: "France", value: "FR" },
	{ label: "PUD No 2 of Grant County", value: "GCPD" },
	{ label: "Greece", value: "GR" },
	{ label: "Gainesville Regional Utilities", value: "GVL" },
	{ label: "Hydro Quebec", value: "HQ" },
	{ label: "Homestead (City of)", value: "HST" },
	{ label: "Hungary", value: "HU" },
	{ label: "Ireland", value: "IE" },
	{ label: "Independent Electricity System Operator (Ontario)", value: "IESO" },
	{ label: "Imperial Irrigation District", value: "IID" },
	{ label: "Idaho Power Co", value: "IPCO" },
	{ label: "New England ISO", value: "ISONE" },
	{ label: "Italy", value: "IT" },
	{ label: "Italy", value: "ITA" },
	{ label: "JEA Jacksonville Florida", value: "JEA" },
	{ label: "Los Angeles Dept of Water & Power", value: "LDWP" },
	{ label: "Louisville Gas & Electric Co", value: "LGEE" },
	{ label: "Lithuania", value: "LT" },
	{ label: "Latvia", value: "LV" },
	{ label: "Midcontinent Independent System Operator", value: "MISO" },
	{ label: "Northwestern Energy", value: "MPCO" },
	{ label: "New South Wales", value: "NEM_NSW" },
	{ label: "Queensland", value: "NEM_QLD" },
	{ label: "South Australia", value: "NEM_SA" },
	{ label: "Tasmania", value: "NEM_TAS" },
	{ label: "Victoria", value: "NEM_VIC" },
	{ label: "Nevada Power Co", value: "NEVP" },
	{ label: "Netherlands", value: "NL" },
	{ label: "Norway", value: "NO" },
	{ label: "New York ISO", value: "NYISO" },
	{ label: "PacifiCorp East", value: "PACE" },
	{ label: "PacifiCorp West", value: "PACW" },
	{ label: "Portland General Electric", value: "PGE" },
	{ label: "PJM Interconnection", value: "PJM" },
	{ label: "Poland", value: "PL" },
	{ label: "Public Service Co of New Mexico", value: "PNM" },
	{ label: "Public Service Co of Colorado", value: "PSCO" },
	{ label: "Puget Sound Energy Inc", value: "PSEI" },
	{ label: "Portugal", value: "PT" },
	{ label: "Romania", value: "RO" },
	{ label: "Serbia", value: "RS" },
	{ label: "South Carolina Public Service Authority", value: "SC" },
	{ label: "Dominion Energy South Carolina", value: "SCEG" },
	{ label: "Seattle City Light", value: "SCL" },
	{ label: "Sweden", value: "SE" },
	{ label: "Seminole Electric Cooperative", value: "SEC" },
	{ label: "Slovenia", value: "SI" },
	{ label: "Slovakia", value: "SK" },
	{ label: "Southern Co Services Inc", value: "SOCO" },
	{ label: "Southwestern Power Administration", value: "SPA" },
	{ label: "Southwest Power Pool", value: "SPP" },
	{ label: "Salt River Project", value: "SRP" },
	{ label: "Tallahassee FL (City of)", value: "TAL" },
	{ label: "Tampa Electric Co", value: "TEC" },
	{ label: "Tucson Electric Power Co", value: "TEPC" },
	{ label: "Turlock Irrigation District", value: "TID" },
	{ label: "Tacoma Power", value: "TPWR" },
	{ label: "Tennessee Valley Authority", value: "TVA" },
	{ label: "United Kingdom", value: "UK" },
	{ label: "WAPA Rocky Mountain Region", value: "WACM" },
	{ label: "WAPA Desert Southwest Region", value: "WALC" },
	{ label: "WAPA Upper Great Plains West", value: "WAUW" },
	{ label: "Western Australia", value: "WEM" },
	{ label: "United States", value: "US" },
	{ label: "Australia", value: "AU" },
	{ label: "Canada", value: "CA" }
];

export const REGIONS_MAP: Record<string, string> = {
	AECI: "Associated Electric Coop Inc",
	AESO: "Alberta Electric System Operator",
	AT: "Austria",
	AVA: "Avista Corp",
	AZPS: "Arizona Public Service Co",
	BANC: "Balancing Authority of Northern California",
	BCHYDRO: "BC Hydro",
	BE: "Belgium",
	BPA: "Bonneville Power Administration",
	CAISO: "California Independent System Operator",
	CHPD: "PUD No 1 of Chelan County",
	CPLE: "Duke Energy Progress East",
	CPLW: "Duke Energy Progress West",
	CZ: "Czechia",
	DE: "Germany",
	DK: "Denmark",
	DOPD: "PUD No 1 of Douglas County",
	DUK: "Duke Energy Carolinas",
	EE: "Estonia",
	EL: "Greece",
	ELE: "El Paso Electric",
	ERCOT: "ERCOT ISO",
	ES: "Spain",
	FI: "Finland",
	FMPP: "Florida Municipal Power Pool",
	FPC: "Duke Energy Florida",
	FPL: "Florida Power & Light",
	FR: "France",
	GCPD: "PUD No 2 of Grant County",
	GR: "Greece",
	GVL: "Gainesville Regional Utilities",
	HQ: "Hydro Quebec",
	HST: "Homestead (City of)",
	HU: "Hungary",
	IE: "Ireland",
	IESO: "Independent Electricity System Operator (Ontario)",
	IID: "Imperial Irrigation District",
	IPCO: "Idaho Power Co",
	ISONE: "New England ISO",
	IT: "Italy",
	ITA: "Italy",
	JEA: "JEA Jacksonville Florida",
	LDWP: "Los Angeles Dept of Water & Power",
	LGEE: "Louisville Gas & Electric Co",
	LT: "Lithuania",
	LV: "Latvia",
	MISO: "Midcontinent Independent System Operator",
	MPCO: "Northwestern Energy",
	NEM_NSW: "New South Wales",
	NEM_QLD: "Queensland",
	NEM_SA: "South Australia",
	NEM_TAS: "Tasmania",
	NEM_VIC: "Victoria",
	NEVP: "Nevada Power Co",
	NL: "Netherlands",
	NO: "Norway",
	NYISO: "New York ISO",
	PACE: "PacifiCorp East",
	PACW: "PacifiCorp West",
	PGE: "Portland General Electric",
	PJM: "PJM Interconnection",
	PL: "Poland",
	PNM: "Public Service Co of New Mexico",
	PSCO: "Public Service Co of Colorado",
	PSEI: "Puget Sound Energy Inc",
	PT: "Portugal",
	RO: "Romania",
	RS: "Serbia",
	SC: "South Carolina Public Service Authority",
	SCEG: "Dominion Energy South Carolina",
	SCL: "Seattle City Light",
	SE: "Sweden",
	SEC: "Seminole Electric Cooperative",
	SI: "Slovenia",
	SK: "Slovakia",
	SOCO: "Southern Co Services Inc",
	SPA: "Southwestern Power Administration",
	SPP: "Southwest Power Pool",
	SRP: "Salt River Project",
	TAL: "Tallahassee FL (City of)",
	TEC: "Tampa Electric Co",
	TEPC: "Tucson Electric Power Co",
	TID: "Turlock Irrigation District",
	TPWR: "Tacoma Power",
	TVA: "Tennessee Valley Authority",
	UK: "United Kingdom",
	WACM: "WAPA Rocky Mountain Region",
	WALC: "WAPA Desert Southwest Region",
	WAUW: "WAPA Upper Great Plains West",
	WEM: "Western Australia",
	US: "United States",
	AU: "Australia",
	CA: "Canada"
};

export const PLATFORM_TYPES: SelectOption[] = [
	{ label: "On premise", value: PlatformType.ON_PREMISE },
	{ label: "Cloud Computing", value: PlatformType.CLOUD_COMPUTING }
];

export const STORAGE_TYPES: SelectOption[] = [
	{ label: "SSD", value: "SSD" },
	{ label: "HDD", value: "HDD" }
];

export const CLOUD_PROVIDERS: SelectOption[] = [
	{ label: "Amazon Web Services", value: "aws" },
	{ label: "Google Cloud Platform", value: "gcp" }
];

export const PREDICTION_WINDOWS: SelectOption[] = [
	{
		label: "3 Months",
		value: "3M"
	},
	{
		label: "6 Months",
		value: "6M"
	},
	{
		label: "9 Months",
		value: "9M"
	},
	{
		label: "1 Year",
		value: "1Y"
	}
];

export const RESOURCE_TYPES: SelectOption[] = [
	{
		label: "Applications",
		value: PATHS.SOFTWARE_ASSETS
	},
	{
		label: "Hardware assets",
		value: PATHS.HARDWARE_ASSETS
	}
];

export const CIO_PERIOD_OPTIONS: SelectOption[] = [
	{
		label: "Fiscal Year",
		value: "FY"
	},
	{
		label: "Current Year",
		value: "CURR_Y"
	},
	{
		label: "Current Quarter",
		value: "CURR_Q"
	},
	{
		label: "Current Semester",
		value: "CURR_S"
	},
	{
		label: "Last Year",
		value: "LAST_Y"
	},
	{
		label: "Last Quarter",
		value: "LAST_Q"
	},
	{
		label: "Last Semester",
		value: "LAST_S"
	}
];

export const CIO_SWITCH_OPTIONS: SelectOption[] = [
	{
		label: "By Domain",
		value: "TYPE"
	},
	{
		label: "By Scope",
		value: "SCOPE"
	}
];

export const CIO_CHIPS_BY_TYPE: ChipOption[] = [
	{
		label: "APPLICATIONS",
		value: 0,
		selected: true
	},
	{
		label: "CLOUD",
		value: 1
	},
	{
		label: "HARDWARE ASSETS",
		value: 2
	},
	{
		label: "AI",
		value: 3
	}
];

export const CIO_CHIPS_BY_SCOPE: ChipOption[] = [
	{
		label: "Scope 1",
		value: 0,
		selected: true
	},
	{
		label: "Scope 2",
		value: 1
	},
	{
		label: "Scope 3",
		value: 2
	}
];

export const MONTHS = [
	"JAN",
	"FEB",
	"MAR",
	"APR",
	"MAY",
	"JUN",
	"JUL",
	"AUG",
	"SEP",
	"OCT",
	"NOV",
	"DEC"
];

export const DEFAULT_PUE: number = 1.5;

export const DATEPICKER_MIN_DATE = new Date(2023, 0, 1);

export const ORDER_BY_OPTIONS: SelectOption[] = [
	{
		label: "Total emission",
		value: "EMISSION"
	},
	{
		label: "Manufacturer name",
		value: "MANUFACTURER"
	}
];

export const CLOUD_ORDER_BY_OPTIONS: SelectOption[] = [
	{
		label: "Total emission",
		value: "EMISSION"
	},
	{
		label: "Cloud provider",
		value: "CLOUD_PROVIDER"
	}
];

export const CLOUD_ORDER_BY_LOCATION_OPTIONS: SelectOption[] = [
	{
		label: "Total emission",
		value: "EMISSION"
	},
	{
		label: "Location",
		value: "LOCATION"
	}
];

export const EMISSION_TYPE_OPTIONS: SelectOption[] = [
	{
		label: "Total emissions",
		value: "TOTAL_EMISSIONS"
	},
	{
		label: "Scope2 emissions",
		value: "SCOPE2_EMISSIONS"
	},
	{
		label: "Scope3 emissions",
		value: "SCOPE3_EMISSIONS"
	}
];

export const INPUT_FLOAT_REGEXP = /^(-?\d+)(\.\d{0,3})?/;

export const INPUT_INTEGER_REGEXP = /^-?\d+$/;

export const MAP_CHART_MIN_RANGE = 0;

export const MAP_CHART_MAX_RANGE = 1000;
